import { useState } from "react";

import {
  faBriefcase,
  faShieldHalved,
} from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "motion/react";

import { Pill } from "src/base-components/Pill";
import { useCasePropertiesUpdatedTable } from "src/cases/caseView/history/events/CasePropertiesTable";
import { DecisionTraceButton } from "src/cases/caseView/history/events/DecisionTraceButton";
import {
  CommentBox,
  HistorySingleEntry,
} from "src/cases/caseView/history/HistoryItemFrame";
import { InlineTable } from "src/cases/common/InlineTable";
import { useCase } from "src/cases/queries/cases";
import { CaseHistoryEvent } from "src/cases/queries/casesTypes";
import { useCaseQueue } from "src/cases/queries/queues";
import { getCaseQueueIcon } from "src/cases/queues/utils";
import { Skeleton } from "src/design-system/Skeleton";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseCreated: React.FC<{
  entry: CaseHistoryEvent<"case_created">;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);

  const templateVersion = caseData?.data.template_version;

  const [isExpanded, setIsExpanded] = useState(false);

  const { columns, rows } = useCasePropertiesUpdatedTable(
    entry.event_data.case_properties,
    templateVersion,
  );

  const comment = entry.event_data.comment;

  const content = (
    <>
      {comment && (
        <CommentBox>
          <span className="font-inter-normal-12px text-gray-700">
            {comment}
          </span>
        </CommentBox>
      )}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <m.div
            animate={{ opacity: 1, height: "auto" }}
            // Hide the vertical scrollbar but keep the horizontal scrollbar
            className="decideScrollbar overflow-x-auto overflow-y-auto [&::-webkit-scrollbar:vertical]:w-0"
            exit={{ opacity: 0, height: 0 }}
            initial={{ opacity: 0, height: 0 }}
            layout
          >
            <InlineTable columns={columns} data={rows} />
          </m.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <HistorySingleEntry
      actorId={entry.user_id}
      content={content}
      eventType={entry.event_type}
      headline={
        <>
          Case was created in <CaseCreatedQueuePill entry={entry} /> queue.
        </>
      }
      icon={faBriefcase}
      isExpanded={isExpanded}
      isLast={isLast}
      footerAction={
        <DecisionTraceButton
          dataLoc="case-created-decision-trace"
          decisionId={entry.event_data.related_decision?.decision_id}
          env={caseData?.data.env}
          flowId={entry.event_data.related_decision?.flow_id}
          versionId={entry.event_data.related_decision?.flow_version_id}
        />
      }
      timestamp={entry.timestamp}
      onToggle={setIsExpanded}
    />
  );
};

const CaseCreatedQueuePill: React.FC<{
  entry: CaseHistoryEvent<"case_created">;
}> = ({ entry }) => {
  const { data: queue, isLoading } = useCaseQueue(entry.event_data.queue_id);

  if (isLoading) return <Skeleton height="h-4" width="w-20" />;

  if (!queue) return null;

  return (
    <Pill size="sm" variant="gray">
      <Pill.Icon icon={getCaseQueueIcon(queue?.icon) ?? faShieldHalved} />
      <Pill.Text>{queue?.name ?? "Unknown queue"}</Pill.Text>
    </Pill>
  );
};
